import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scanner } from '@yudiel/react-qr-scanner';
import { useEvents } from '../../context/EventsContext';
import { formatDate, formatPrice, reservationStatuses } from '../../utils/format';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, ScanLine, CheckCircle2, XCircle,
  User, Mail, Phone, CreditCard, Calendar,
  AlertTriangle, PartyPopper
} from 'lucide-react';

const QrScanner = () => {
  const navigate = useNavigate();
  const { findReservationByUuid, markAttended } = useEvents();

  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(true);
  const [attendanceMarked, setAttendanceMarked] = useState(false);
  const [processing, setProcessing] = useState(false);

  const handleScan = async (detectedCodes) => {
    if (!scanning || !detectedCodes?.length) return;
    const code = detectedCodes[0].rawValue;
    if (!code) return;

    setScanning(false);
    setProcessing(true);

    try {
      const result = await findReservationByUuid(code);
      if (result && result.reservation) {
        setScanResult(result);
        setAttendanceMarked(result.reservation.attended);
      } else {
        setScanResult('not_found');
      }
    } catch {
      setScanResult('not_found');
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmAttendance = async () => {
    if (!scanResult || scanResult === 'not_found') return;
    try {
      await markAttended(scanResult.reservation.id);
      setAttendanceMarked(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleReset = () => {
    setScanResult(null);
    setScanning(true);
    setAttendanceMarked(false);
    setProcessing(false);
  };

  const res = scanResult && scanResult !== 'not_found' ? scanResult.reservation : null;
  const event = scanResult && scanResult !== 'not_found' ? scanResult.event : null;
  const resChairs = res?.chairs || [];
  const resTotal = resChairs.reduce((s, c) => s + (parseFloat(c.price) || 0), 0);
  const statusConf = res ? (reservationStatuses[res.status] || reservationStatuses.pending) : null;
  const isConfirmed = res?.status === 'confirmed';

  return (
    <div data-testid="qr-scanner-page" className="min-h-screen bg-[#050505]">
      <header className="border-b border-border bg-secondary/50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3 sm:gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Volver</span>
          </Button>
          <div className="min-w-0">
            <h1 className="text-base sm:text-xl font-serif font-semibold text-foreground truncate">Escanear QR</h1>
            <p className="text-xs sm:text-sm text-muted-foreground truncate">Confirma asistencia de reservaciones</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8">
        {/* Scanner */}
        {scanning && !processing && (
          <div className="space-y-4">
            <div className="bg-secondary border border-border rounded-lg overflow-hidden">
              <div className="aspect-square max-h-[400px] relative">
                <Scanner
                  onScan={handleScan}
                  formats={['qr_code']}
                  components={{ audio: false, torch: false }}
                  styles={{ container: { width: '100%', height: '100%' }, video: { objectFit: 'cover' } }}
                />
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                  <div className="w-48 h-48 border-2 border-primary/50 rounded-2xl" />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <ScanLine className="w-4 h-4 text-primary animate-pulse" />
              <span>Apunta la cámara al código QR de la reservación</span>
            </div>
          </div>
        )}

        {/* Processing */}
        {processing && (
          <div className="text-center py-12 text-muted-foreground">
            <ScanLine className="w-8 h-8 mx-auto animate-pulse text-primary mb-3" />
            <p>Buscando reservación...</p>
          </div>
        )}

        {/* Not Found */}
        {scanResult === 'not_found' && (
          <div className="space-y-6">
            <div className="bg-secondary border border-red-500/30 rounded-lg p-8 text-center">
              <XCircle className="w-12 h-12 mx-auto text-red-400 mb-4" />
              <h2 className="text-lg font-medium text-foreground mb-2">Reservación no encontrada</h2>
              <p className="text-sm text-muted-foreground">El código QR escaneado no corresponde a ninguna reservación registrada.</p>
            </div>
            <Button onClick={handleReset} className="w-full bg-primary hover:bg-accent">
              <ScanLine className="w-4 h-4 mr-2" />Escanear otro código
            </Button>
          </div>
        )}

        {/* Reservation Found */}
        {res && (
          <div className="space-y-5">
            {attendanceMarked && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-5 text-center">
                <PartyPopper className="w-10 h-10 mx-auto text-emerald-400 mb-3" />
                <h2 className="text-lg font-medium text-emerald-400 mb-1">Asistencia Confirmada</h2>
                <p className="text-sm text-muted-foreground">{res.full_name} ha sido registrado/a exitosamente.</p>
              </div>
            )}

            <div className="bg-secondary border border-border rounded-lg overflow-hidden">
              <div className={`px-4 py-2.5 flex items-center justify-between ${isConfirmed ? 'bg-emerald-500/10' : 'bg-amber-500/10'}`}>
                <span className="text-xs font-medium text-muted-foreground">Estado de Reserva</span>
                <Badge className={statusConf.color}>{statusConf.label}</Badge>
              </div>

              <div className="p-5 space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-foreground">{res.full_name}</h3>
                    <p className="text-xs text-muted-foreground font-mono">{res.uuid}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground"><Mail className="w-4 h-4 shrink-0" /><span className="truncate">{res.email}</span></div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground"><Phone className="w-4 h-4 shrink-0" /><span>{res.whatsapp}</span></div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground"><CreditCard className="w-4 h-4 shrink-0" /><span>{res.paymentMethod || '-'}</span></div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground"><Calendar className="w-4 h-4 shrink-0" /><span>{new Date(res.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span></div>
                </div>
              </div>

              {/* Chairs + Event */}
              {event && (
                <div className="px-5 pb-5 border-t border-border pt-4">
                  <div className="flex items-start gap-4">
                    {event.image && <img src={event.image} alt={event.name} className="w-16 h-16 rounded-lg object-cover shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-foreground">{event.name}</h4>
                      <p className="text-xs text-muted-foreground mt-0.5">{formatDate(event.date)} · {event.time} hrs</p>
                      <div className="mt-2 space-y-1">
                        {resChairs.map(c => (
                          <div key={c.id} className="flex justify-between text-xs text-muted-foreground">
                            <span>Silla {c.label}</span>
                            <span>{formatPrice(c.price, event.currency)}</span>
                          </div>
                        ))}
                        <div className="border-t border-border pt-1 flex justify-between text-sm font-medium">
                          <span className="text-foreground">Total</span>
                          <span className="text-primary">{formatPrice(resTotal, event.currency)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {!isConfirmed && !attendanceMarked && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-400">Reserva no confirmada</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Esta reserva tiene estado "{statusConf.label}". Puedes marcar asistencia de todas formas.</p>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              {!attendanceMarked ? (
                <Button data-testid="confirm-attendance-btn" onClick={handleConfirmAttendance} className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-11">
                  <CheckCircle2 className="w-5 h-5 mr-2" />Confirmar Asistencia
                </Button>
              ) : (
                <Button disabled className="flex-1 bg-emerald-800 h-11 opacity-70">
                  <CheckCircle2 className="w-5 h-5 mr-2" />Asistencia Registrada
                </Button>
              )}
              <Button variant="outline" onClick={handleReset} className="border-border text-muted-foreground hover:text-foreground h-11">
                <ScanLine className="w-5 h-5 mr-2" />Escanear otro
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default QrScanner;
